import { Globals } from "./globals";
import { Player } from "./hand";
import { Instruction } from "./instructions";
import { createDecks } from "./randUtil";
import { packetToArrayBuffer, toPacket } from "./serialize";
import { Stack } from "./stack";
import {
  deckIndex,
  GameState,
  InstructionTest,
  Message,
  Opcode,
  Packet,
  PhaseStress,
  PhaseStuck,
  pileIndex,
  RenderGameState,
  slotIndex,
  StatePlaying,
} from "./types";

import { cloneDeep } from "lodash";

export class Network {
  public playerId: number = -1;
  public clientState: GameState = this.emptyGameState(); // client state
  private authoritiveState: GameState = this.emptyGameState(); // acknowledged state by the server
  private pendingInputs: Packet[] = []; // sent inputs yet to be acknowledged
  private receivedInputs: Packet[] = []; // received inputs by the server to be processed
  private socket!: WebSocket; // initialized in connect!!
  private sequenceNumber: number = 0;
  public currentAdjustment: number = -1; // a constant now much faster/slower the simulation should run values < 0 increase speed, > 0 slow down the simulation
  private currentAction: Message | null = null;
  public isActive: boolean = true; // set to false when the game ends
  public now: DOMHighResTimeStamp = performance.now();
  public pendingSimulationTime: number = 0;
  private debugLog: InstructionTest = {
    initial: this.emptyGameState(),
    messages: [],
    expected: this.emptyGameState(),
  };
  private tickTimerId: number | null = null;

  public constructor() {}

  public connect(serverUrl: string): void {
    this.socket = new WebSocket(serverUrl);
    this.socket.binaryType = "arraybuffer";
    this.socket.onopen = () => {
      console.log("Connected to the server");
    };

    this.socket.onmessage = (event: MessageEvent) => {
      const packet: Packet = toPacket(event.data);
      if (packet.message.kind === "start") {
        this.playerId = packet.message.playerId;
        this.now = performance.now();
        if (this.tickTimerId !== null) {
          window.cancelAnimationFrame(this.tickTimerId);
        }
        this.tickTimerId = window.requestAnimationFrame(() => {
          tickTimer(this);
        });
      }
      this.receivedInputs.push(packet);
      console.log(`Received message: ${JSON.stringify(packet)}`);
    };

    this.socket.onclose = () => {
      console.log("Disconnected from the server");
      this.isActive = false;
    };

    this.socket.onerror = (error) => {
      console.error("WebSocket error:", error);
    };
    console.log("set up network");
  }

  private send(msg: Message): void {
    const packet: Packet = {
      tickAdjustmentMs: 0, // unused for client, potential future use: send last adjustment
      tick: Globals.tick,
      message: msg,
    };
    this.pendingInputs.push(packet);
    this.socket.send(packetToArrayBuffer(packet));
  }

  // process all server messages in order
  // this means we can discard pending inputs with tick < serverPacket.tick
  // if we find our own packet we can discard it too
  // returns true if some message by the server
  // did not line up with the ordering of pendingInputs
  private processServerMessages(): boolean {
    let discrepancy = false;
    while (this.receivedInputs.length > 0) {
      const serverPacket = this.receivedInputs.shift()!;
      if (serverPacket.message.kind !== "synchronize") {
        this.debugLog.messages.push(serverPacket);
      }
      if (
        Instruction.tryApply(
          this.authoritiveState,
          serverPacket.message,
          serverPacket.tick,
        ) === undefined
      ) {
        this.debugLog.expected = this.authoritiveState;
        let backendUri: String;
        if (window.location.port === "1234") {
          // local, backend at localhost:
          backendUri = "http://" + window.location.hostname + ":10067/api/v1";
        } else {
          // assume deployed somewhere and backend is available at same host/port as frontend
          backendUri =
            window.location.protocol + "//" + window.location.host + "/api/v1";
        }
        fetch(backendUri + "/game/crashreport", {
          method: "POST",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
          body: JSON.stringify(this.debugLog),
        });
        // https://stackoverflow.com/a/30800715
        // download the crashreport as json
        let dataStr =
          "data:text/json;charset=utf-8," +
          encodeURIComponent(JSON.stringify(this.debugLog, null, 2));
        let downloadAnchorNode = document.createElement("a");
        downloadAnchorNode.setAttribute("href", dataStr);
        downloadAnchorNode.setAttribute(
          "download",
          `stress-bugreport-${Date.now()}` + ".json",
        );
        document.body.appendChild(downloadAnchorNode); // required for firefox
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
        throw new Error(
          `processing server msg: ${JSON.stringify(serverPacket)} at tick ${Globals.tick} failed. current state\n${JSON.stringify(this.authoritiveState)}`,
        );
      }

      // filter out packets that were ignored anyway
      const previousPendingCount: number = this.pendingInputs.length;

      this.pendingInputs = this.pendingInputs.filter(
        (pending: Packet) => pending.tick >= serverPacket.tick,
      );

      // if we are behind when receiving the packet at least lock on to the server tick
      Globals.tick = Math.max(serverPacket.tick, Globals.tick);
      this.currentAdjustment = this.calculateNewAdjustmentMs(
        serverPacket.tickAdjustmentMs,
      );
      // console.log(`currentAdjustment: ${this.currentAdjustment}`);

      // we do not care about synchornoize as we did not put them in the pendingInputs
      // but we need to detect a discrepancy if this caused some packts from the pending
      // queue to be discrarded, which means they have not been acknowledged by the server
      if (serverPacket.message.kind === "synchronize") {
        if (this.pendingInputs.length < previousPendingCount) {
          discrepancy = true;
        }
        continue;
      }

      const oldestClientPacket = this.pendingInputs.at(0);
      if (
        oldestClientPacket &&
        oldestClientPacket.tick === serverPacket.tick &&
        this.messageEquals(oldestClientPacket.message, serverPacket.message)
      ) {
        this.pendingInputs.shift();
      } else {
        discrepancy = true;
      }
    }
    return discrepancy;
  }

  private calculateNewAdjustmentMs(adjMs: number): number {
    if (adjMs <= -128) return -4;
    if (adjMs <= -64) return -2;
    if (adjMs <= -8) return -1;
    if (adjMs <= 8) return 0;
    if (adjMs <= 24) return 1;
    if (adjMs < 127) return 2;
    return 4;
  }

  // resimulates pending inputs and sets the new client state
  private resimulateClientInputs(): void {
    const newClientState: GameState = cloneDeep(this.authoritiveState);
    console.log(`pendingInputs: ${JSON.stringify(this.pendingInputs)}`);
    for (let input of this.pendingInputs) {
      if (!Instruction.tryApply(newClientState, input.message, input.tick)) {
        console.log(`could not re-simulate: ${JSON.stringify(input.message)}`);
        break;
      }
    }
    this.clientState = newClientState;
  }

  // shallow compare two messages (are not nested anyway)
  private messageEquals(a: Message, b: Message): boolean {
    const keysA = Object.keys(a) as Array<keyof Message>;
    const keysB = Object.keys(b) as Array<keyof Message>;
    if (keysA.length !== keysB.length) {
      return false;
    }
    return keysA.every((key) => a[key] === b[key]);
  }

  // creates an unitialized state
  private emptyGameState(): GameState {
    const [playerZero, playerOne] = createDecks(0);

    // Pull initial middle cards from the players' decks to start
    const leftStart = playerZero.pop()!;
    const rightStart = playerOne.pop()!;

    return {
      playerZero: new Player(playerZero),
      playerOne: new Player(playerOne),
      leftPile: new Stack<number>(leftStart),
      rightPile: new Stack<number>(rightStart),
      sidePile: new Stack<number>(...[]),
      gamePhase: { kind: "normal" },
      winnerId: null,
    };
  }

  // advances the simulation by one tick
  // 1. read server messages and update state accordingly
  // 2. try apply current action
  // 3. if valid send this action to the server
  // 4. increment tick
  public runTick(): void {
    if (this.processServerMessages()) {
      this.resimulateClientInputs();
    }
    if (this.currentAction != null) {
      const executedInstruction = Instruction.tryApply(
        this.clientState,
        this.currentAction,
        Globals.tick,
      );
      if (executedInstruction) {
        this.send(executedInstruction);
      }
      this.currentAction = null;
    } else {
      // if we sent nothing ensure that we stay in sync bypass the input buffer
      if (Globals.tick % Globals.config.network.sync_rate_ticks === 0) {
        const packet: Packet = {
          tickAdjustmentMs: 0,
          tick: Globals.tick,
          message: {
            kind: "synchronize",
            opcode: Opcode.Synchronize,
            sequenceNumber: this.sequenceNumber++,
          },
        };
        this.socket.send(packetToArrayBuffer(packet));
      }
    }
    this.checkForTickEvents();
    Globals.tick++;
  }

  // checks if we should trigger some event on this tick.
  // This can be
  // - a move has completed
  // - a delay (like stress) has expired
  private checkForTickEvents(): void {
    const phase = this.clientState.gamePhase;
    switch (phase.kind) {
      case "normal":
        const playerState = this.playerCards(this.clientState).state;
        if (playerState.kind === "playing") {
          this.checkForMoveComplete(playerState);
        }
        const opponentState = this.opponentCards(this.clientState).state;
        if (opponentState.kind === "playing") {
          this.checkForMoveComplete(opponentState);
        }
        break;
      case "stuck":
        this.checkForStuckEnd(phase);
        break;
      case "stress":
        this.checkForStressEnd(phase);
        break;
    }
  }

  private checkForStressEnd(phase: PhaseStress): void {
    if (
      Globals.tick - phase.startTick >
      Globals.config.game.stress_duration_ticks
    ) {
      let message: Message = {
        kind: "endStress",
        opcode: Opcode.EndStress,
      };
      this.applyWithoutSend(message);
    }
  }
  private checkForStuckEnd(phase: PhaseStuck): void {
    if (
      Globals.tick - phase.startTick >
      Globals.config.game.stuck_duration_ticks
    ) {
      let message: Message = {
        kind: "endStuck",
        opcode: Opcode.EndStuck,
      };
      this.applyWithoutSend(message);
    }
  }

  // no network action is performed, we get the result from the server
  private checkForMoveComplete(movingCard: StatePlaying): void {
    if (
      movingCard !== undefined &&
      Globals.tick - movingCard.startTick >=
        Globals.config.client.move_duration_ticks
    ) {
      let message: Message | undefined = {
        kind: "moveSuccess",
        opcode: Opcode.MoveSuccess,
        srcSlot: movingCard.srcSlot,
        dstPile: movingCard.dstPile,
      };
      this.applyWithoutSend(message);
    }
  }

  // try to apply an instruction only client side/do not send anything over the network
  private applyWithoutSend(message: Message) {
    const appliedMessage = Instruction.tryApply(
      this.clientState,
      message,
      Globals.tick,
    );
    if (appliedMessage) {
      const packet: Packet = {
        tickAdjustmentMs: 0,
        tick: Globals.tick,
        message: appliedMessage,
      };
      this.pendingInputs.push(packet);
    }
  }

  private playerCards(state: GameState): Player {
    if (this.playerId === 0) {
      return state.playerZero;
    } else {
      return state.playerOne;
    }
  }
  private opponentCards(state: GameState): Player {
    if (this.playerId === 0) {
      return state.playerOne;
    } else {
      return state.playerZero;
    }
  }

  // player tries to play a card
  public handlePileClick = (pile: pileIndex): void => {
    let player = this.playerCards(this.clientState);
    if (player.state.kind === "selected") {
      let selectedCard = player.state.slot;
      if (this.playerId === 1) {
        selectedCard += 8;
      }
      this.currentAction = {
        kind: "moveStart",
        opcode: Opcode.MoveStart,
        srcSlot: selectedCard as slotIndex,
        dstPile: pile,
      };
    }
  };

  public handleMoveComplete(move: StatePlaying): void {
    this.currentAction = {
      kind: "moveSuccess",
      opcode: Opcode.MoveSuccess,
      srcSlot: move.srcSlot,
      dstPile: move.dstPile,
    };
  }

  // player tries to play a card
  public handleDeckUncoverClick = (_cards: Player): void => {
    const deckIdx: deckIndex = this.playerId === 0 ? 4 : 12;
    this.currentAction = {
      kind: "uncover",
      opcode: Opcode.Uncover,
      srcDeck: deckIdx,
    };
  };

  // player tries to select a card, always works
  public handleSlotClick = (cardIndex: slotIndex): void => {
    console.log(`clicked on index ${cardIndex}`);
    if (this.playerId === 1) {
      cardIndex += 8;
    }
    this.currentAction = {
      kind: "select",
      opcode: Opcode.Select,
      slot: cardIndex,
    };
  };

  // player clicks stress
  public handleStressClick = (): void => {
    this.currentAction = {
      kind: "stress",
      opcode: Opcode.Stress,
      playerId: this.playerId as 0 | 1,
    };
  };

  public renderState(): RenderGameState {
    const s = this.clientState;
    return {
      player: this.playerId === 0 ? s.playerZero : s.playerOne,
      opponent: this.playerId === 0 ? s.playerOne : s.playerZero,
      leftPile: s.leftPile,
      rightPile: s.rightPile,
      sidePile: s.sidePile,
      gamePhase: s.gamePhase,
      winner: s.winnerId === this.playerId,
      onPileClick: this.handlePileClick,
      onUncoverClick: this.handleDeckUncoverClick,
      onSlotClick: this.handleSlotClick,
      onStressClick: this.handleStressClick,
    };
  }
}

function tickTimer(network: Network): void {
  if (!network.isActive) {
    return; // game is no longer active
  }
  const now: DOMHighResTimeStamp = performance.now();
  const TICK_RATE_MS = Globals.config.game.tickrate_ms;
  // how much are we off
  const deltaTime = now - network.now;

  let accumulator = deltaTime + network.pendingSimulationTime;

  // Run as many ticks as needed to catch up to real time TODO: add failsafe
  // if this gets too large
  while (accumulator >= TICK_RATE_MS + network.currentAdjustment) {
    network.runTick();
    accumulator -= TICK_RATE_MS + network.currentAdjustment;
  }

  if (Globals.tick % 100 === 0) {
    console.log(`current tick: ${Globals.tick}, dt: ${deltaTime}`);
  }

  network.now = now;
  network.pendingSimulationTime = accumulator;
  window.requestAnimationFrame(() => {
    tickTimer(network);
  });
}
